/**
 * What the technician card's Device section shows about a checked-in device,
 * beyond its make / model / serial (those are `formatDeviceIdentityParts`).
 *
 * Two tiers: the item-status answers are always visible — what came in with
 * the machine is what the customer asks about at collection — and everything
 * else of interest sits behind the row's "More" toggle.
 *
 * Answers are reported as recorded, never interpreted. Item-status rows do not
 * share a polarity ("Power adapter: Yes" is good, "Wobbles: Yes" is not), which
 * is also why the printed ticket sheet lists them verbatim. Damage rows do:
 * every one asks whether damage was seen, so only those answered Yes (or
 * carrying a note) are findings.
 */
import type { CheckInDraftJob, CheckInServiceMode } from '@techgitter/contracts';
import { INSPECTION_SCHEMA, inspectionToDraft } from './inspectionSchema';
import { formatCallTime } from './callFormat';

export interface DeviceCheckAnswer {
  label: string;
  answer: 'Yes' | 'No';
  notes: string | null;
}

export interface DeviceDamageFinding {
  label: string;
  notes: string | null;
}

/** One label/value line of the expanded detail. */
export interface DeviceDetailField {
  label: string;
  value: string;
}

export interface JobDeviceDetails {
  /** Answered item-status rows, in schema order. Always visible. */
  itemStatus: DeviceCheckAnswer[];
  /** Behind the toggle, in display order; empty values are left out. */
  fields: DeviceDetailField[];
  photoCount: number;
}

/** Same wording as the composer's Service Mode radios. */
const SERVICE_MODE_LABELS: Record<CheckInServiceMode, string> = {
  diagnose_and_quote: 'Diagnose & Quote',
  prepaid_in_full: 'Pre-paid in Full',
};

const AI_FIELD_LABELS: Record<string, string> = {
  manufacturer: 'manufacturer',
  serialNumber: 'serial',
  modelNumber: 'model number',
  emcNumber: 'EMC number',
};

const clean = (value: string | null | undefined): string => (value ?? '').trim();

/** "Power adapter: Yes (65W Dell)". */
export function formatCheckAnswer(row: DeviceCheckAnswer): string {
  const notes = clean(row.notes);
  return `${row.label}: ${row.answer}${notes ? ` (${notes})` : ''}`;
}

function inspectionStatusText(dj: CheckInDraftJob): string {
  if (dj.inspectionStatus === 'complete') {
    const by = clean(dj.inspectionCompletedBy);
    const at = dj.inspectionCompletedAt ? formatCallTime(dj.inspectionCompletedAt) : '';
    return ['Complete', by && `by ${by}`, at].filter(Boolean).join(' · ');
  }
  return dj.inspectionStatus === 'in_progress' ? 'In progress' : 'Not started';
}

export function jobDeviceDetails(dj: CheckInDraftJob): JobDeviceDetails {
  const schema = INSPECTION_SCHEMA[dj.deviceType];
  const draft = dj.inspection ? inspectionToDraft(dj.deviceType, dj.inspection) : null;

  const itemStatus: DeviceCheckAnswer[] = [];
  const damage: DeviceDamageFinding[] = [];
  if (draft && schema) {
    for (const row of schema.itemStatusRows) {
      const check = draft.itemStatusChecks[row.key];
      if (check?.status) {
        itemStatus.push({ label: row.label, answer: check.status === 'yes' ? 'Yes' : 'No', notes: check.notes });
      }
    }
    for (const row of schema.damageRows) {
      const check = draft.damageChecks[row.key];
      if (check?.status === 'yes' || clean(check?.notes)) {
        damage.push({ label: row.label, notes: check?.notes ?? null });
      }
    }
  }

  // A sealed box skips the damage rows, so "none noted" would claim a look
  // that never happened; and before any row is answered there is no verdict.
  const damageAnswered =
    !!draft && !draft.brandNewInBox && Object.values(draft.damageChecks).some(c => c?.status);
  const damageText = damage.length
    ? damage.map(d => (clean(d.notes) ? `${d.label} — ${clean(d.notes)}` : d.label)).join('; ')
    : damageAnswered && schema?.damageRows.length
      ? 'None noted'
      : '';

  // A key in aiFilled means the field still holds exactly what the photo read.
  const aiUnchecked = Object.keys(draft?.aiFilled ?? {})
    .filter(k => (draft?.aiFilled as Record<string, unknown>)[k])
    .map(k => AI_FIELD_LABELS[k] ?? k);

  const candidates: DeviceDetailField[] = [
    { label: 'Service summary', value: clean(dj.serviceSummary) },
    { label: 'Service mode', value: SERVICE_MODE_LABELS[dj.serviceMode] ?? '' },
    { label: 'Requirements', value: clean(dj.requirements) },
    { label: 'Customer-visible details', value: clean(dj.customerVisibleDetails) },
    { label: 'Internal notes', value: clean(dj.internalNotes) },
    { label: 'Damage noted', value: damageText },
    { label: 'Other notes', value: clean(draft?.otherNotes) },
    {
      label: 'Read from photo',
      value: aiUnchecked.length ? `${aiUnchecked.join(', ')} — not yet checked against the label` : '',
    },
    { label: 'Inspection', value: inspectionStatusText(dj) },
  ];

  return {
    itemStatus,
    fields: candidates.filter(f => f.value),
    photoCount: dj.photos?.length ?? 0,
  };
}
